import { useCallback, useEffect, useMemo, useState } from "react";
import { FormAction } from "@komune-io/g2-forms";
import { BasicProps } from "@komune-io/g2-themes";
import {
  RealizationModel,
  RealizationCreateCommand,
  RealizationUpdateCommand,
  Realization,
} from "../../../model/realizationModel";
import { useBuildingTypeAllQuery } from "../../../../../referentiels/buildingType/api/BuildingTypeApi";
import { useOwnershipTypeAllQuery } from "../../../../../referentiels/ownershipType/api/OwnershipTypeApi";
import { FormComposable, FormComposableField, useFeedback } from "@tracabois/components";
import { useFormComposable } from "@komune-io/g2-composable";
import urlSlug from "url-slug";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Grid,
} from "@mui/material";
import { MyLocation, Map } from "@mui/icons-material";

export interface RealizationFormLabel {
  submit?: string;
  cancel?: string;
}

export interface RealizationFormProps extends BasicProps {
  item?: RealizationModel | Realization;
  labels?: RealizationFormLabel;
  onSubmit?: (item: RealizationCreateCommand | RealizationUpdateCommand) => Promise<boolean> | boolean;
  onCancel?: () => void;
  showBdaFields?: boolean;
}

// Simple map picker component
const CoordinatesMapPicker = ({
  latitude,
  longitude,
  onCoordinatesChange,
}: {
  latitude?: number;
  longitude?: number;
  onCoordinatesChange: (lat: number, lng: number) => void;
}) => {
  const [showMap, setShowMap] = useState(false);

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onCoordinatesChange(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Impossible d'obtenir votre position. Vérifiez les autorisations de localisation.");
        }
      );
    } else {
      alert("La géolocalisation n'est pas supportée par votre navigateur.");
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Tooltip title="Utiliser ma position actuelle">
        <IconButton size="small" onClick={handleGetCurrentLocation} color="primary">
          <MyLocation />
        </IconButton>
      </Tooltip>
      {latitude && longitude && (
        <Typography variant="caption" color="text.secondary">
          {Number(latitude).toFixed(6)}, {Number(longitude).toFixed(6)}
        </Typography>
      )}
    </Box>
  );
};

export const RealizationForm = (props: RealizationFormProps) => {
  const { item, onSubmit, onCancel, showBdaFields = true } = props;
  const feedback = useFeedback();
  const labels = {
    submit: props.labels?.submit ?? "Créer",
    cancel: props.labels?.cancel ?? "Annuler",
  };

  const onSubmitMemoized = useCallback(
    async (values: RealizationCreateCommand | RealizationUpdateCommand) => {
      if (onSubmit) {
        // Transform string values to proper types for the API
        const transformedValues = {
          ...values,
          // Convert numeric string fields to numbers
          deliveryYear: values.deliveryYear ? parseInt(String(values.deliveryYear), 10) : undefined,
          surface: values.surface ? parseFloat(String(values.surface)) : undefined,
          volume: values.volume ? parseFloat(String(values.volume)) : undefined,
        };
        const result = await onSubmit(transformedValues as RealizationCreateCommand | RealizationUpdateCommand);
        feedback.setFeedback(result);
        return result;
      }
      return false;
    },
    [onSubmit]
  );

  const buildingTypeQuery = useBuildingTypeAllQuery();
  const ownershipTypeQuery = useOwnershipTypeAllQuery();

  const buildingTypeOptions = useMemo(
    () =>
      (buildingTypeQuery.data?.items ?? []).map((item) => ({
        key: item.id,
        label: item.name,
      })),
    [buildingTypeQuery.data]
  );

  const ownershipTypeOptions = useMemo(
    () =>
      (ownershipTypeQuery.data?.items ?? []).map((item) => ({
        key: item.id,
        label: item.name,
      })),
    [ownershipTypeQuery.data]
  );

  const fields: FormComposableField[] = useMemo((): FormComposableField[] => {
    const baseFields: FormComposableField[] = [
      {
        key: "name",
        name: "name",
        type: "textField",
        label: "Nom de la réalisation *",
        validator: (value?: string) => {
          const trimmed = (value ?? "").trim();
          if (!trimmed) return "Vous devez renseigner le nom" as string;
          return undefined;
        },
        params: {},
      },
    ];

    if (showBdaFields) {
      baseFields.push(
        {
          key: "buildingType.id",
          name: "buildingType.id",
          type: "select",
          label: "Type de bâtiment",
          params: {
            options: buildingTypeOptions,
          },
        },
        {
          key: "ownershipType.id",
          name: "ownershipType.id",
          type: "select",
          label: "Type de propriété",
          params: {
            options: ownershipTypeOptions,
          },
        },
        {
          key: "commune",
          name: "commune",
          type: "textField",
          label: "Commune",
          params: {
            placeholder: "Ex: Grenoble",
          },
        },
        {
          key: "codePostal",
          name: "codePostal",
          type: "textField",
          label: "Code postal",
          params: {
            placeholder: "Ex: 38000",
          },
        },
        {
          key: "deliveryYear",
          name: "deliveryYear",
          type: "textField",
          label: "Année de livraison",
          params: {
            textFieldType: "number",
            placeholder: "Ex: 2023",
          },
        },
        {
          key: "surface",
          name: "surface",
          type: "textField",
          label: "Surface (m²)",
          params: {
            textFieldType: "number",
            placeholder: "Ex: 2500",
          },
        },
        {
          key: "volume",
          name: "volume",
          type: "textField",
          label: "Volume Bois des Alpes (m³)",
          params: {
            textFieldType: "number",
            placeholder: "Ex: 450",
          },
        }
      );
    }

    baseFields.push(
      {
        key: "coordinates.latitude",
        name: "coordinates.latitude",
        type: "textField",
        label: "Latitude",
        validator: (value?: number) => {
          if (value === undefined) return undefined;
          const lat = Number.parseFloat(`${value}`);
          if (!(lat >= -90 && lat <= 90)) return "Vous devez renseigner une latitude valide";
          return undefined;
        },
        params: {
          placeholder: "Ex: 45.1885",
          textFieldType: "number",
        },
      },
      {
        key: "coordinates.longitude",
        name: "coordinates.longitude",
        type: "textField",
        label: "Longitude",
        validator: (value?: number) => {
          if (value === undefined) return undefined;
          const long = Number.parseFloat(`${value}`);
          if (!(long >= -180 && long <= 180)) return "Vous devez renseigner une longitude valide";
          return undefined;
        },
        params: {
          placeholder: "Ex: 5.7245",
          textFieldType: "number",
        },
      },
      {
        key: "artIndex",
        name: "artIndex",
        type: "textField",
        label: "Index Art",
        params: {},
      },
      {
        key: "redirectPath",
        name: "redirectPath",
        type: "textField",
        label: "Identifiant unique du lien Qrcode",
        validator: (value?: string) => {
          const trimmed = (value ?? "").trim();
          if (trimmed.length != 0 && trimmed.length < 3)
            return "Vous devez renseigner au moins 3 caractères" as string;
          return undefined;
        },
        params: {
          placeholder: "Ex: centre-culturel-puy-guillaume",
        },
      }
    );

    if (showBdaFields) {
      baseFields.push({
        key: "externalId",
        name: "externalId",
        type: "textField",
        label: "Identifiant externe (import CSV)",
        params: {
          placeholder: "Ex: EXT-001",
        },
      });
    }

    return baseFields;
  }, [showBdaFields, buildingTypeOptions, ownershipTypeOptions]);

  const formState = useFormComposable<RealizationCreateCommand | RealizationUpdateCommand>({
    onSubmit: onSubmitMemoized,
    formikConfig: {
      initialValues: {
        id: item?.id,
        name: item?.name,
        description: item?.description,
        coordinates: item?.coordinates,
        // @ts-ignore
        artIndex: item?.artIndex || item?.indicators?.artIndex,
        redirectPath: item?.qrcode?.name,
        // BDA fields
        // @ts-ignore
        buildingType: item?.buildingType ? { id: item.buildingType } : undefined,
        // @ts-ignore
        ownershipType: item?.ownershipType ? { id: item.ownershipType } : undefined,
        // @ts-ignore
        commune: item?.commune,
        // @ts-ignore
        codePostal: item?.codePostal,
        // @ts-ignore
        deliveryYear: item?.deliveryYear,
        // @ts-ignore
        surface: item?.surface,
        // @ts-ignore
        volume: item?.volume,
        // @ts-ignore
        externalId: item?.externalId,
      },
    },
  });

  useEffect(() => {
    !item?.qrcode?.name &&
      formState.values.name &&
      formState.setFieldValue("redirectPath", urlSlug(formState.values.name));
  }, [formState.values.name]);

  const handleCoordinatesChange = useCallback(
    (lat: number, lng: number) => {
      formState.setFieldValue("coordinates.latitude", lat);
      formState.setFieldValue("coordinates.longitude", lng);
    },
    [formState]
  );

  const actions: FormAction[] = useMemo(
    () => [
      {
        key: "cancel",
        label: labels.cancel,
        onClick: onCancel,
        variant: "text",
      },
      {
        key: "submit",
        label: labels.submit,
        success: feedback.isSuccess,
        fail: feedback.isFailure,
        onClick: formState.submitForm,
      },
    ],
    [onCancel, labels.cancel, labels.submit, feedback, formState]
  );

  return (
    <Box>
      <FormComposable fields={fields} formState={formState} actions={actions} />

      {/* Coordinates helper */}
      <Box sx={{ mt: -2, mb: 2, ml: 1 }}>
        <CoordinatesMapPicker
          latitude={formState.values.coordinates?.latitude}
          longitude={formState.values.coordinates?.longitude}
          onCoordinatesChange={handleCoordinatesChange}
        />
      </Box>
    </Box>
  );
};
